<template>
  <div
    class="c-outreach-contact-connect"
    data-testid="outreach-contact-connect-twitter"
  >
    <QSpinner
      v-if="loading"
      :size="30"
    />
    <div
      v-else
      class="c-outreach-contact-connect-twitter__content"
    >
      <div class="c-outreach-contact-connect-twitter__content-header">
        {{ $t("outreach_contact_connect_twitter.integrate_heading") }}
      </div>
      <div class="c-outreach-contact-connect-twitter__content-body">
        <i18n-t
          keypath="outreach_contact_connect_twitter.integrate_body"
          scope="global"
        >
          <template #platformName>
            {{ appConfig.platformName }}
          </template>
          <template #newline>
            <br />
          </template>
        </i18n-t>
      </div>
      <BaseButton
        color="primary"
        icon="img:https://assets.streem.com.au/icons/engage/50x50_twitter.svg"
        :label="$t('outreach_contact_connect_twitter.connect_label')"
        new-design
        to="/outreach/organisation/integrations"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import { getAppConfig } from "shared/helpers/useAppConfig";
import TwitterSocialIntegration from "shared/resources/TwitterSocialIntegration";
import { type Maybe } from "shared/types";

const emit = defineEmits<{
  "set-social-integration-id": [id: string];
}>();

const appConfig = getAppConfig();

const loading = ref(false);
// shallowRef keeps the items typed as resource instances; the list is only
// ever replaced, never mutated
const twitterIntegrations = shallowRef<TwitterSocialIntegration[]>([]);

function isExpired({
  expiredAt,
}: Pick<TwitterSocialIntegration, "expiredAt">): boolean {
  return Boolean(expiredAt && new Date(expiredAt) <= new Date());
}

// An expired integration alongside a working one must not hide the feed
const activeIntegration = computed<Maybe<TwitterSocialIntegration>>(() =>
  twitterIntegrations.value.find((integration) => !isExpired(integration))
);

async function loadTwitterIntegrations(): Promise<void> {
  try {
    loading.value = true;

    twitterIntegrations.value = (
      await TwitterSocialIntegration.where({
        archivedAt: { eq: "null" },
        isOutreachAccount: { eq: true },
      })
        .order({ expiredAt: "desc" })
        .all()
    ).data;

    if (activeIntegration.value?.id) {
      emit("set-social-integration-id", activeIntegration.value.id);
    }
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_connect_twitter.load_integration_error"),
      error
    );
  } finally {
    loading.value = false;
  }
}

onMounted(loadTwitterIntegrations);
</script>

<style scoped lang="scss">
.c-outreach-contact-connect-twitter {
  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    &-header {
      font-size: 20px;
      font-weight: 700;
    }

    &-body {
      text-align: center;
      font-size: 14px;
      padding-bottom: 27px;
    }
  }
}
</style>
